import React from 'react';
import { ExternalLink, Briefcase, Building2, MapPin } from 'lucide-react';
import { ResponsiveOverlay } from '@/components/ui/ResponsiveOverlay';

export interface JobImportPayload {
  jobTitle: string;
  company: string;
  location: string;
  description: string;
  source: string;
  url: string;
}

interface JobImportModalProps {
  job: JobImportPayload | null;
  isOpen: boolean;
  onClose: () => void;
  onTailor: (job: JobImportPayload) => void;
}

export const JobImportModal: React.FC<JobImportModalProps> = ({ job, isOpen, onClose, onTailor }) => {
  if (!job) return null;

  return (
    <ResponsiveOverlay isOpen={isOpen} onClose={onClose} variant="modal" className="sm:max-w-3xl w-full">
      <div className="flex flex-col gap-4 p-4 sm:p-6 bg-slate-50 dark:bg-slate-950/50 max-h-[90vh]">
        
        {/* Card 1: Job Header Info */}
        <div className="bg-white dark:bg-slate-900 rounded-2xl p-6 border border-slate-200 dark:border-slate-800 shadow-sm flex flex-col gap-4 shrink-0">
          <div className="flex items-center gap-3">
            <span className="px-2.5 py-1 rounded-md text-xs font-medium bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800/50">
              via {job.source.charAt(0).toUpperCase() + job.source.slice(1)}
            </span>
            <a
              href={job.url}
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors"
            >
              View original posting <ExternalLink className="w-3 h-3" />
            </a>
          </div>
          
          <div>
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-800 dark:text-slate-100 mb-3">{job.jobTitle || 'Unknown Position'}</h2>
            <div className="flex flex-wrap items-center gap-4 text-sm text-slate-600 dark:text-slate-400">
              <div className="flex items-center gap-1.5">
                <Building2 className="w-4 h-4 opacity-70" />
                <span className="font-semibold text-slate-700 dark:text-slate-300">{job.company || 'Unknown Company'}</span>
              </div>
              {job.location && (
                <div className="flex items-center gap-1.5">
                  <MapPin className="w-4 h-4 opacity-70" />
                  <span>{job.location}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Card 2: Job Description */}
        <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm flex flex-col min-h-0 overflow-hidden">
          <div className="p-4 sm:p-5 border-b border-slate-100 dark:border-slate-800 shrink-0 bg-slate-50/50 dark:bg-slate-900/50">
            <h3 className="text-base font-semibold text-slate-800 dark:text-slate-200 flex items-center gap-2">
              <Briefcase className="w-4 h-4 text-slate-400" />
              Job Description
            </h3>
          </div>
          <div className="p-4 sm:p-6 overflow-y-auto">
            <div className="whitespace-pre-wrap font-sans text-sm leading-relaxed text-slate-600 dark:text-slate-400">
              {(job.description || '').replace(/\n\s*\n/g, '\n\n').replace(/\n{3,}/g, '\n\n') || 'No description found...'}
            </div>
          </div>
        </div>

        {/* Card 3: Actions */}
        <div className="flex flex-col sm:flex-row-reverse gap-3 shrink-0 mt-2">
          <button
            onClick={() => onTailor(job)}
            className="w-full sm:flex-1 py-3.5 px-8 text-sm sm:text-base font-semibold bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl shadow-sm transition-all flex items-center justify-center gap-2 cursor-pointer"
          >
            Tailor My Resume →
          </button>
          <button
            onClick={onClose}
            className="w-full sm:flex-1 py-3.5 px-6 text-sm font-semibold text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-800 rounded-xl transition-colors border border-slate-200 dark:border-slate-800 bg-white dark:bg-transparent cursor-pointer"
          >
            Cancel
          </button>
        </div>

      </div>
    </ResponsiveOverlay>
  );
};
